/**
 * Deterministic trigger system (docs/ARCHITECTURE.md §5).
 *
 * Triggers are level objects; the sim activates them positionally (player x
 * crosses trigger x, once per attempt) or via spawn triggers. Activations
 * enqueue tweens that advance per tick with fixed-point-free but
 * order-deterministic arithmetic. Move/rotate are additive and applied
 * incrementally, so a `stop` freezes cleanly and undo/restore is exact.
 *
 * Cycle safety: activation budget per tick; overflow raises a diagnostic flag
 * (surfaced in the editor) and drops further spawns instead of freezing.
 */
import { TICK_HZ } from '../core/tick';
import type { EasingName, LevelObject, TriggerKind } from '../../format/types';

/** Per-tick trigger activation budget (cycle/runaway protection). */
export const TRIGGER_BUDGET_PER_TICK = 128;

export interface TriggerDef {
  id: number;
  x: number;
  y: number;
  kind: TriggerKind;
  /** Group/channel target (0 when N/A). */
  target: number;
  /** Groups this trigger belongs to (spawn targeting). */
  groups: number[];
  /** Only activatable via spawn triggers, never positionally. */
  spawnOnly: boolean;
  dx: number;
  dy: number;
  degrees: number;
  alpha: number;
  visible: boolean;
  hex: string;
  durationTicks: number;
  delayTicks: number;
  zoom: number;
  strength: number;
  factor: number;
  scale: number;
  /** 0 = unlimited (for spawn-fired triggers); positional fire once anyway. */
  maxActivations: number;
  easing: EasingName;
  /** Area/enter radius (units). */
  radius: number;
  /** Gradient second color. */
  hex2: string;
  vertical: boolean;
  /** camera:edge side. */
  side: string;
  /** item:spawn-if spawn-group id. */
  value: number;
  /** Keyframe path segments (block offsets converted to units, ticks). */
  path: Array<{ dx: number; dy: number; ticks: number }>;
  loop: boolean;
}

export function triggerFromObject(obj: LevelObject): TriggerDef | null {
  if (obj.type !== 'trigger') return null;
  const p = obj.props ?? {};
  const num = (k: string, d = 0): number => (typeof p[k] === 'number' ? (p[k] as number) : d);
  const kind = (typeof p.kind === 'string' ? p.kind : 'move') as TriggerKind;
  return {
    id: obj.id,
    x: obj.x,
    y: obj.y,
    kind,
    target: num('target', num('channel', 0)),
    groups: obj.groups ?? [],
    spawnOnly: p.spawnOnly === true,
    dx: num('dx') * 30,
    dy: num('dy') * 30,
    degrees: num('degrees'),
    alpha: num('alpha', 1),
    visible: p.visible !== false,
    hex: typeof p.hex === 'string' ? p.hex : '#ffffff',
    durationTicks: Math.max(0, Math.round(num('duration') * TICK_HZ)),
    delayTicks: Math.max(0, Math.round(num('delay') * TICK_HZ)),
    zoom: num('zoom', 1),
    strength: num('strength', 8),
    factor: num('factor', 1),
    scale: num('scale', 1),
    maxActivations: Math.max(0, Math.floor(num('maxActivations', 0))),
    easing: (typeof p.easing === 'string' ? p.easing : 'linear') as EasingName,
    radius: num('radius', 6) * 30,
    hex2: typeof p.hex2 === 'string' ? p.hex2 : '#000000',
    vertical: p.vertical !== false,
    side: typeof p.side === 'string' ? p.side : 'bottom',
    value: num('value', 0),
    path: parsePath(typeof p.path === 'string' ? p.path : ''),
    loop: p.loop === true,
  };
}

function parsePath(text: string): Array<{ dx: number; dy: number; ticks: number }> {
  if (!text) return [];
  const out: Array<{ dx: number; dy: number; ticks: number }> = [];
  for (const seg of text.split(';')) {
    const [dx, dy, t] = seg.split(',').map(Number);
    if (dx === undefined || dy === undefined || t === undefined) continue;
    if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(t)) continue;
    out.push({ dx: dx * 30, dy: dy * 30, ticks: Math.max(1, Math.round(t * TICK_HZ)) });
  }
  return out;
}

export function ease(name: EasingName, t: number): number {
  const x = Math.max(0, Math.min(1, t));
  switch (name) {
    case 'linear':
      return x;
    case 'in':
      return x * x;
    case 'out':
      return 1 - (1 - x) * (1 - x);
    case 'inout':
      return x < 0.5 ? 2 * x * x : 1 - 2 * (1 - x) * (1 - x);
  }
}

// --- Sim-side trigger state (all plain data: snapshot/hash friendly) ---------

export interface GroupState {
  dx: number;
  dy: number;
  rot: number; // degrees, render-side rotation
  scale: number; // render-side scale about each object's center
  alpha: number;
  visible: boolean;
}

export interface ActiveTween {
  kind: TriggerKind;
  target: number;
  startTick: number;
  endTick: number;
  easing: EasingName;
  /** Totals for additive kinds; absolutes for from/to kinds. */
  dx: number;
  dy: number;
  degrees: number;
  fromAlpha: number;
  toAlpha: number;
  fromZoom: number;
  toZoom: number;
  fromHex: string;
  toHex: string;
  strength: number;
  fromScale: number;
  toScale: number;
  /** Incrementally applied portions (move/rotate/camera offset). */
  appliedX: number;
  appliedY: number;
  appliedDeg: number;
  /** followy: player y at the previous advance. */
  aux: number;
  /** Tick when paused by a pause trigger; −1 = running. */
  pausedAt: number;
  /** Keyframe path (immutable after creation; safely shared across clones). */
  path?: Array<{ dx: number; dy: number; ticks: number }>;
  loop?: boolean;
}

export interface CameraState {
  zoom: number;
  dx: number;
  dy: number;
  /** View roll, degrees. */
  rot: number;
  /** Static lock: when on, the camera holds (staticX, staticY) world coords. */
  staticOn: boolean;
  staticX: number;
  staticY: number;
  /** Shake amplitude, units; decays linearly to zero by shakeEndTick. */
  shakeAmp: number;
  shakeEndTick: number;
  shakeDur: number;
}

/** Render-side area field around a point with radius falloff. */
export interface AreaEffect {
  triggerId: number;
  kind: 'move' | 'rotate' | 'scale' | 'fade' | 'tint';
  x: number;
  y: number;
  radius: number;
  dx: number;
  dy: number;
  degrees: number;
  scale: number;
  alpha: number;
  hex: string;
}

/** Screen-enter presentation: objects animate in over `width` units. */
export interface EnterFx {
  kind: 'move' | 'rotate' | 'scale' | 'fade';
  dx: number;
  dy: number;
  degrees: number;
  scale: number;
  width: number;
}

export interface GradientFx {
  hexA: string;
  hexB: string;
  alpha: number;
  vertical: boolean;
}

export interface CameraEdges {
  minX: number | null;
  maxX: number | null;
  minY: number | null;
  maxY: number | null;
}

export interface TriggerRuntimeState {
  /** One-shot ledger: trigger object ids already activated this attempt. */
  fired: number[];
  /** Activation counts (enforces maxActivations on spawn-fired triggers). */
  counts: Record<number, number>;
  areas: AreaEffect[];
  enter: EnterFx | null;
  gradient: GradientFx | null;
  edges: CameraEdges | null;
  /** Item counters (keys, pickups) — reset each attempt. */
  items: Record<number, number>;
  /** Scheduled spawn firings. */
  pending: Array<{ tick: number; group: number }>;
  tweens: ActiveTween[];
  groups: Record<number, GroupState>;
  channels: Record<number, string>;
  camera: CameraState;
  /** Full-screen shader effect scalars (0 = off), keyed by effect name. */
  shaderFx: Record<string, number>;
  timewarp: number;
  /** Set when the per-tick activation budget was exceeded (editor warns). */
  overflow: boolean;
}

export function initialTriggerState(): TriggerRuntimeState {
  return {
    fired: [],
    counts: {},
    areas: [],
    enter: null,
    gradient: null,
    edges: null,
    items: {},
    pending: [],
    tweens: [],
    groups: {},
    channels: {},
    camera: {
      zoom: 1,
      dx: 0,
      dy: 0,
      rot: 0,
      staticOn: false,
      staticX: 0,
      staticY: 0,
      shakeAmp: 0,
      shakeEndTick: 0,
      shakeDur: 1,
    },
    shaderFx: {},
    timewarp: 1,
    overflow: false,
  };
}

export function cloneTriggerState(s: TriggerRuntimeState): TriggerRuntimeState {
  return {
    fired: [...s.fired],
    counts: { ...s.counts },
    areas: s.areas.map((a) => ({ ...a })),
    enter: s.enter ? { ...s.enter } : null,
    gradient: s.gradient ? { ...s.gradient } : null,
    edges: s.edges ? { ...s.edges } : null,
    items: { ...s.items },
    pending: s.pending.map((p) => ({ ...p })),
    tweens: s.tweens.map((t) => ({ ...t })),
    groups: Object.fromEntries(Object.entries(s.groups).map(([k, v]) => [k, { ...v }])),
    channels: { ...s.channels },
    camera: { ...s.camera },
    shaderFx: { ...s.shaderFx },
    timewarp: s.timewarp,
    overflow: s.overflow,
  };
}

export function groupState(s: TriggerRuntimeState, id: number): GroupState {
  let g = s.groups[id];
  if (!g) {
    g = { dx: 0, dy: 0, rot: 0, scale: 1, alpha: 1, visible: true };
    s.groups[id] = g;
  }
  return g;
}

/** Interpolate two #rrggbb colors. */
export function lerpHex(a: string, b: string, t: number): string {
  const pa = [
    parseInt(a.slice(1, 3), 16),
    parseInt(a.slice(3, 5), 16),
    parseInt(a.slice(5, 7), 16),
  ];
  const pb = [
    parseInt(b.slice(1, 3), 16),
    parseInt(b.slice(3, 5), 16),
    parseInt(b.slice(5, 7), 16),
  ];
  const mix = pa.map((va, i) => Math.round(va + ((pb[i] as number) - va) * t));
  return `#${mix.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
